import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import { getSpanWithColor } from "../../../utils/CardUtils";
import { formatDefaultDateTime, formatPrice } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import InfinityGridOrdersCards from "../../common/cardContainers/InfinityGridOrdersCards";
import { AlertIcon } from "../../common/dialogs/DialogBoxComponent";
import FormField from "../../common/forms/FormField";
import ListComponent from "../../common/lists/ListComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import OrderMissingSizesTooltip from "./ordersComponents/OrderMissingSizesTooltip";
import OrderPreview from "./ordersComponents/OrderPreview";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;

const StyledPopup = styled.div`
    &.destination-warehouse-popup-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        width: 100%;
    }

    .warehouse-popup-description {
        color: var(--text-icons-gray-100, #d1d1d1);
        text-align: center;
        width: 100%;
    }

    .warehouse-popup-description-main {
        display: block;
        font-size: 14px;
        line-height: 16px;
        margin-bottom: 16px;
    }

    .warehouse-popup-description-note {
        display: block;
        font-size: 12px;
        line-height: 16px;
    }

    .warehouse-order-summary {
        background: var(--backgrounds-lines-n-600, #10141b);
        border-radius: 4px;
        color: var(--text-icons-gray-100, #d1d1d1);
        font-size: 12px;
        line-height: 14px;
        max-width: 100%;
        padding: 10px;
        text-align: left;
        width: 187px;
    }

    .warehouse-order-summary-label {
        color: var(--text-icons-gray-100, #d1d1d1);
    }

    .warehouse-order-summary-value {
        color: var(--text-icons-green-500, #00bb00);
    }

    .warehouse-dropdown-wrapper {
        width: 100%;
        margin-bottom: 32px;
        text-align: left;
    }
`;
// #endregion

// #region CONSTANTS
const DEFAULT_STATUSES_FILTER = [
    { name: "Exported", value: "ExportedByRetailBrand", selected: true },
    { name: "Deleted", value: "Deleted", selected: false }
];

export const orderStatusColors = [
    {
        colorClass: "text-blue-600",
        forValues: ["Exported"]
    },
    {
        colorClass: "text-green-500",
        forValues: ["Ready to export"]
    },
    {
        colorClass: "text-orange-500",
        forValues: [
            "Pending rules",
            "Pending master data",
            "Missing sizes in size system",
            "Missing info",
            "Processing"
        ]
    },
    {
        colorClass: "text-red-500",
        forValues: ["Failed to export", "Deleted"]
    },
    {
        colorClass: "text-gray-300",
        forValues: ["--"]
    }
];

export const ButtonsEnabledRules = (buttonName, status) => {
    const mappedStatus = getMappedStatus(status)?.toLowerCase();

    switch (buttonName?.toLowerCase()) {
        case "export":
            if (mappedStatus === "missing info") return false;
            if (mappedStatus === "processing") return false;
            return mappedStatus === "ready to export" || mappedStatus === "failed to export";
        case "rules":
            //Keep RULES button enabled in all order status.
            if (!mappedStatus || mappedStatus === "--") return false;
            return true;
        case "delete":
            return true;
        case "view":
            return true;
        default:
            return false;
    }
};
// #endregion

export default function ArchiveOrdersPage() {
    document.title = `${APP_TITLE} - Archive Orders`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [listNumRows, setListNumRows] = useState(10);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const gridRef = useRef(null);
    const selectedDestinationWarehouseRef = useRef(null);
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const navigate = useNavigate();
    // #endregion

    // #region STATES
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOrdersExport, setIsLoadingOrdersExport] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [allStatusList, setAllStatusList] = useState(() => DEFAULT_STATUSES_FILTER.map(item => ({ ...item })));
    const [filteredStatus, setFilteredStatus] = useState(() => allStatusList.filter(item => item.selected));
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const [showOrderPreview, setShowOrderPreview] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);

    const filters = {
        retailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        searchText: searchText,
        statuses: filteredStatus?.map(item => item?.value)
    };
    const gridQueryKey = ["ArchiveOrdersGrid", filters];
    const listQueryKey = ["ArchiveOrdersList", filters];
    // #endregion

    // #region METHODS - MAIN WINDOW HEADER
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(gridQueryKey);
            gridRef.current?.reset?.(gridQueryKey);
        }
    };

    const onSearch = text => {
        setSearchText(text);
    };

    const onRetailBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredRetailBrand(selections);
        setAllRetailBrandsList(dropdownDataWithSelections);
    };

    const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    };

    const onStatusChange = (selections, dropdownDataWithSelections) => {
        setFilteredStatus(selections);
        setAllStatusList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        //const defaultRetailBrand = sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");
        //const defaultLabelBrand = sessionStorage.getItem("currentLabelBrand") || localStorage.getItem("defaultLabelBrand");
        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultRetailBrand
                };
            });
            //const defaultRetailBrandObj = retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand(retailBrands);
        }

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map((item, index) => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }
                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultLabelBrand
                };
            });
            //const defaultLabelBrandObj = labelBrands?.find(item => item?.value === defaultLabelBrand) || labelBrands?.[0];

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        setRequestsEnabled(true);
    };
    // #endregion

    // #region METHODS - CONFIRMATION DIALOGS
    const showConfirmExportDialog = (row, warehouse) => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            title: "Export Confirmation",
            message: `You’re about to export Order #${row?.orderNumber} with ${row?.totalBarcodes} barcodes.<br />
            If any items don’t already exist in your system, they’ll be created automatically. <br />
            Stock will remain pending until delivery. <br /><br />
            Ready to continue?`,
            headingText: "Just Checking...",
            headingColor: "var(--text-icons-blue-500, #00aeef)",
            onConfirmClicked: () => {
                exportToPOS(row, warehouse);
            },
            showConfirm: true,
            showCancel: true,
            confirmText: "CONTINUE",
            cancelText: "CANCEL",
            width: "441px",
            confirmState: "secondary"
        });
    };

    const showPOSSetupNeededDialog = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS setup needed!",
            message:
                "Your POS system is not yet configured to connect to BOtag.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "POS SETUP",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate("/stores/pos");
            }
        });
    };

    const showPOSConnectionNeededDialog = storeOurID => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS connection needed!",
            message: "BOtag is not yet connected to this store.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "CONNECT STORE",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate(`/stores/edit/${storeOurID}#stock-sharing`);
                sessionStorage.setItem("scrollToPOS", true);
            }
        });
    };
    // #endregion

    // #region METHODS - BUTTON ACTIONS AND PROPS
    const onClickPreview = row => {
        setOrderDetails(row);
        setShowOrderPreview(true);
    };

    const onClickExport = row => {
        const isPOSSetupNeeded = row?.retailBrandPosStatus !== "Connected";
        const isPOSConnectionNeeded = row?.storePosStatus !== "Connected";

        if (isPOSSetupNeeded) {
            showPOSSetupNeededDialog();
            return;
        }
        if (isPOSConnectionNeeded) {
            showPOSConnectionNeededDialog(row?.storeOurID);
            return;
        }

        showDestinationWarehousePopup(row);
        //showConfirmExportDialog(row);
    };

    const exportToPOS = async (row, warehouse, onSuccessClosePopup = false) => {
        const payload = {
            retailBrandID: row?.retailBrandID,
            storeID: row?.storeID,
            supplierOrderIDs: [row?._id],
            warehouseIdFromPOS: warehouse?.id
        };

        const res = await Library.makeApiRequest({
            endpoint: "X/SupplierOrders/exportSupplierOrderToPOS",
            data: payload,
            setLoader: setIsLoadingOrdersExport,
            onErrorShowMsg: false
        });

        onClickRefreshAction();

        if (res?.data?.status !== 200) {
            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                show: true,
                headingText: "ATTENTION!",
                headingColor: "var(--text-icons-orange-500, #f90)",
                title: "Order not exported!",
                message: `There was a problem exporting this order. Several causes may have prevented this order to go through. 
                          Try again in a few minutes or contact support@botag.no for assistance.`,
                type: "error",
                showConfirm: false,
                showCancel: true,
                cancelText: "CLOSE",
                titleColor: "var(--text-icons-orange-500, #f90)"
            });

            return;
        }
        const { supplierOrdersExportResult, productsExportResult } = res.data?.data || {};
        if (onSuccessClosePopup) {
            setGenericConfirmationPopup({
                show: false
            });
        }

        const createdOrdersCount = supplierOrdersExportResult?.createdCount || 0;
        const alreadyCreatedOrdersCount = supplierOrdersExportResult?.alreadyCreatedCount || 0;
        const createdProductsCount = productsExportResult?.createdCount || 0;
        const barcodesExportedCount = productsExportResult?.barcodesExported || 0;

        if (createdOrdersCount === 1) {
            const htmlMessage = `
                <div style="color: #d1d1d1; text-align: center; font-family: sans-serif;">
                    <div style="font-size: 14px; line-height: 22px;">
                        ${barcodesExportedCount} products exported.<br />
                        ${createdProductsCount} products created.
                    </div>

                    <div style="font-size: 12px; line-height: 15px; margin-top: 4px;">
                        (if products already exist in your system we don’t<br />create them again)
                    </div>

                    <div style="font-size: 14px; line-height: 22px; margin-top: 4px;">
                        Stock pending delivery.
                    </div>
                </div>`;

            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                show: true,
                headingText: "SUCCESS!",
                headingColor: "var(--text-icons-blue-500, #00aeef)",
                title: "Order exported successfully",
                message: htmlMessage,
                type: "success",
                showConfirm: false,
                showCancel: true,
                cancelText: "CLOSE"
            });
        } else if (alreadyCreatedOrdersCount > 0)
            Library.showToastMessage({
                message: "Order already exported!",
                type: "error"
            });
        else
            Library.showToastMessage({
                message: "Something went wrong!",
                type: "error"
            });
    };

    const showDestinationWarehousePopup = async row => {
        const res = await Library.makeApiRequest({
            endpoint: "X/SupplierOrders/getWarehousesFromPOS",
            data: { retailBrandID: row?.retailBrandID, storeID: row?.storeID },
            setLoader: setIsLoading,
            onErrorShowMsg: false
        });

        if (res?.data?.status !== 200) return;

        // Response is an array of {id, name}.
        const warehouses = res?.data?.data?.warehouses || [];
        const storeWarehouseID = res?.data?.data?.storeWarehouseID || null;
        if (warehouses?.length === 0) {
            Library.showToastMessage({ message: "No warehouses found!", type: "error" });
            return;
        }

        if (warehouses?.length === 1) {
            showConfirmExportDialog(row, warehouses[0]);
            return;
        }

        const storeWarehouseExists = storeWarehouseID && warehouses.some(w => w?.id === storeWarehouseID);
        const defaultWarehouse = storeWarehouseExists
            ? warehouses.find(w => w?.id === storeWarehouseID)
            : warehouses[0];
        selectedDestinationWarehouseRef.current = defaultWarehouse;

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            useIconSrc: AlertIcon(),
            headingText: "ATTENTION!!",
            headingColor: "var(--text-icons-orange-500)",
            title: "Select destination warehouse",
            useAlternativeLayout: (
                <ChooseDestinationWarehouseContent
                    warehouseData={row}
                    warehouses={warehouses}
                    storeWarehouseID={storeWarehouseID}
                    onWarehouseChange={warehouse => {
                        selectedDestinationWarehouseRef.current = warehouse;
                    }}
                />
            ),
            confirmText: "EXPORT",
            cancelText: "CANCEL",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-orange-500)",
            afterConfirmWillResetPopup: false,
            onConfirmClicked: () => {
                const selectedWarehouse = selectedDestinationWarehouseRef.current ?? warehouses[0];
                exportToPOS(row, selectedWarehouse, true);
            }
        });
    };

    const onClickSetRules = item => {
        navigate(`/products/pos-export?labelBrandID=${item?.labelBrandID}`);
    };

    const onClickDelete = item => {
        const isDeleted = item?.status?.toLowerCase() === "deleted";
        const messageHtml =
            isDeleted
                ? "This order will be removed from the archive and permanently deleted. You can't undo this process"
                : "This order will be marked as deleted. You can find it in the archive by selecting the Deleted status.";

        const title = isDeleted ? "DELETING ORDER" : "Deleting order?";

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            useIconSrc: AlertIcon(),
            headingText: "ATTENTION!!",
            headingColor: "var(--text-icons-orange-500)",
            title: title,
            messageHtml: messageHtml,
            confirmText: "YES",
            cancelText: "NO",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                deleteSupplierOrder(item);
            }
        });
    };

    const deleteSupplierOrder = async item => {
        const forceDelete = item?.status?.toLowerCase() === "deleted";
        const res = await Library.makeApiRequest({
            endpoint: "SupplierOrders/deleteBulkOrder",
            data: { supplierOrderID: item?._id, forceDelete: forceDelete },
            setLoader: setIsLoading,
            onErrorShowMsg: true
        });

        if (res?.data?.status !== 200) return;

        if (!forceDelete)
            Library.showToastMessage({ message: "Order deleted successfully", type: "success" });
        else
            Library.showToastMessage({
                title: "Deleted Order",
                message: "This order was permanently deleted and can't be recovered",
                type: "success"
            });
        onClickRefreshAction();
    };

    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Order nr.",
            selector: row => row?.orderNumber ?? "--"
        },
        {
            name: "From brand",
            selector: row => row?.labelBrandName ?? "--"
        },
        {
            name: "To store",
            selector: row => row?.storeName ?? "--"
        },
        {
            name: "Imported on",
            selector: row => formatDefaultDateTime(row?.createdAt)
        },
        {
            name: "Imported from",
            selector: row => "Email" //row?.importedFromPosName ?? "--"
        },
        {
            name: "Total",
            selector: row => formatPrice(row?.totalPriceIncludingVAT, "kr", "--")
        }
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            onFiltersLoaded,
            allRetailBrandsList,
            onRetailBrandChange,
            filteredRetailBrand,
            allLabelBrandsList,
            onLabelBrandChange,
            filteredLabelBrand,
            allStatusList,
            onStatusChange,
            filteredStatus
        );
    }, [
        props?.layoutType,
        allRetailBrandsList,
        filteredRetailBrand,
        allLabelBrandsList,
        filteredLabelBrand,
        allStatusList,
        filteredStatus
    ]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="orders-page">
            <FullScreenLoader isLoading={isLoading || isLoadingOrdersExport} />

            <FullScreenOverlay show={showOrderPreview}>
                <OrderPreview onCloseClick={() => setShowOrderPreview(false)} orderDetails={orderDetails} />
            </FullScreenOverlay>

            {viewLayoutStyle === "Grid" && requestEnabled && (
                <InfinityGridOrdersCards
                    key={JSON.stringify(gridQueryKey)}
                    ref={gridRef}
                    minColumnWidth="238px"
                    limit={12}
                    queryKey={gridQueryKey}
                    filters={filters}
                    requestEnabled={requestEnabled}
                    onDeleteClick={onClickDelete}
                    onViewClick={onClickPreview}
                    onExportClick={onClickExport}
                    onRulesClick={onClickSetRules}
                    isArchivePage={true}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Orders"
                    columns={listColumns}
                    api={{
                        endpoint: "getBulkOrdersData",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows,
                            filters: filters
                        },
                        useCommonPostRequest: true,
                        commonContext: "SupplierOrders",
                        onSuccess: data => {
                            const items = data?.result ?? [];
                            const mappedItems = items?.map(item => {
                                return { ...item, mappedStatus: getMappedStatus(item?.status) };
                            });
                            return mappedItems;
                        }
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        includeActions: true,
                        extraActions: [
                            {
                                title: "Delete",
                                onClick: onClickDelete,
                                isDisabled: item => !ButtonsEnabledRules("delete", item?.status)
                            },
                            {
                                title: "View",
                                onClick: onClickPreview,
                                isDisabled: item => !ButtonsEnabledRules("view", item?.status)
                            },
                            {
                                title: "Rules",
                                onClick: onClickSetRules,
                                isDisabled: item => !ButtonsEnabledRules("rules", item?.status)
                            },
                            {
                                title: "Export",
                                onClick: onClickExport,
                                isDisabled: item => !ButtonsEnabledRules("export", item?.status)
                            }
                        ]
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "mappedStatus",
                        statusColors: orderStatusColors,
                        width: "220px",
                        renderStatus: ({ row, defaultStatusView }) => (
                            <div className="flex align-items-center gap-4px">
                                {defaultStatusView}
                                <OrderMissingSizesTooltip data={row} placement="left" />
                            </div>
                        )
                    }}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    onFiltersLoaded,
    allRetailBrandsList,
    onRetailBrandChange,
    filteredRetailBrand,
    allLabelBrandsList,
    onLabelBrandChange,
    filteredLabelBrand,
    allStatusList,
    onStatusChange,
    filteredStatus
) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Order number, brand" /> }
    ]);

    props.setControlBarCenterBtns([
        { to: "/orders", active: false, disabled: false, title: "ORDERS" },
        { to: "/orders/reorders", active: false, disabled: false, title: "REORDERS" },
        { to: "/orders/archive", active: true, disabled: false, title: "ARCHIVE" }
    ]);

    props.setControlBarRightBtns([
        { to: "/orders", active: true, disabled: false, title: "View", icon: "eye" },
        { to: "/orders/calendar", active: false, disabled: true, title: "Calendar", icon: "calendar" },
        { to: "/orders/details", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <ArchiveOrdersHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: allLabelBrandsList?.length === 1 ? "singleSelect" : "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true
                }}
            />
        )
    });
}
// #endregion

// #region EXPORTED FUNCTIONS

export const getMappedStatus = status => {
    const statusObject = DEFAULT_STATUSES_FILTER.find(item => item.value?.toLowerCase() === status?.toLowerCase());
    if (statusObject) return statusObject.name;
    return "--";
};

export const getOrderSpanWithColor = status => {
    const mappedStatus = getMappedStatus(status);

    return getSpanWithColor(mappedStatus, orderStatusColors);
};
// #endregion

// #region SMALL COMPONENTS
const ArchiveOrdersHeader = props => {
    const statusFilterReadyRef = useRef(false);

    useEffect(() => {
        // Child mount effects notify an empty selection before FormField initializes it.
        statusFilterReadyRef.current = true;
        return () => {
            statusFilterReadyRef.current = false;
        };
    }, []);

    const onStatusChange = (selections, dropdownDataWithSelections) => {
        if (!statusFilterReadyRef.current) return;
        props.secondOrganize.onDropdownChange(selections, dropdownDataWithSelections);
    };

    return (
        <MainWindowHeader {...props} secondOrganize={{ ...props.secondOrganize, onDropdownChange: onStatusChange }} />
    );
};

const ChooseDestinationWarehouseContent = ({
    warehouseData,
    warehouses = [],
    onWarehouseChange = () => {},
    storeWarehouseID = null
}) => {
    const [warehouseDropdownData, setWarehouseDropdownData] = useState(() => {
        const storeWarehouseExists = storeWarehouseID && warehouses.some(w => w?.id === storeWarehouseID);
        return warehouses.map((warehouse, index) => {
            const isStoreWarehouse = storeWarehouseID && warehouse?.id === storeWarehouseID;
            const isDefault = storeWarehouseExists ? isStoreWarehouse : index === 0;
            return {
                ...warehouse,
                value: warehouse?.id,
                name: warehouse?.name ?? warehouse?.id ?? "--",
                selected: isDefault
            };
        });
    });

    const orderSummary = [
        { label: "Brand:", value: warehouseData?.labelBrandName ?? "--" },
        { label: "Order:", value: warehouseData?.orderNumber ?? "--" },
        { label: "Barcodes:", value: warehouseData?.totalBarcodes ?? "--" },
        { label: "Items:", value: warehouseData?.totalOrderProducts ?? warehouseData?.totalItems ?? "--" },
        { label: "Total amount:", value: formatPrice(warehouseData?.totalPriceIncludingVAT, "kr", "--") }
    ];

    const onDestinationWarehouseChange = data => {
        setWarehouseDropdownData(prev =>
            prev.map(warehouse => ({
                ...warehouse,
                selected: warehouse?.value === data?.value
            }))
        );

        const selectedWarehouse = warehouses.find(
            warehouse => warehouse?.id === data?.value || `${warehouse?.id}` === `${data?.value}`
        );

        onWarehouseChange(selectedWarehouse);
    };

    return (
        <StyledPopup className="destination-warehouse-popup-content">
            <div className="warehouse-popup-description">
                <span className="warehouse-popup-description-main">
                    Choose the warehouse where this order should be exported in the POS.
                </span>
                <span className="warehouse-popup-description-note">
                    If any items don&apos;t already exist in your system, they&apos;ll be created automatically. Stock
                    will remain pending until delivery.
                </span>
            </div>

            <div className="warehouse-order-summary">
                {orderSummary.map(item => (
                    <div key={item.label} className="warehouse-order-summary-row">
                        <span className="warehouse-order-summary-label">{item.label} </span>
                        <span className="warehouse-order-summary-value">{item.value}</span>
                    </div>
                ))}
            </div>

            <div className="warehouse-dropdown-wrapper">
                <FormField
                    type="dropdown"
                    dropdownData={warehouseDropdownData}
                    varName="destinationWarehouse"
                    isDropdownSearchable={warehouseDropdownData?.length > 6}
                    isDropdownMultiselect={false}
                    placeholder="Select warehouse"
                    onDropdownChange={onDestinationWarehouseChange}
                    formFieldColumSize={1}
                    requiredType="none"
                    styling={{
                        formFieldHeight: "37px",
                        formFieldWidth: "100%",
                        dropdownOverlayTop: "45px"
                    }}
                    disableTooltip={true}
                    disableAssistiveText={true}
                />
            </div>
        </StyledPopup>
    );
};
// #endregion
